import { useState, useEffect } from "react";
import { Menu, X, LogIn } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { NAV } from "@/constants/testIds";
import MemberLoginModal from "@/components/MemberLoginModal";

const sections = [
  { id: "about", key: "about" },
  { id: "why", key: "why" },
  { id: "values", key: "values" },
  { id: "locations", key: "locations" },
  { id: "activities", key: "activities" },
  { id: "leadership", key: "leadership" },
  { id: "faq", key: "faq" },
];

const Header = () => {
  const { t, lang, setLang, langs } = useLanguage();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onDark = !scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-anthracite/90 backdrop-blur-xl backdrop-saturate-150 border-b border-gold/15"
          : "bg-transparent"
      }`}
    >
      <div className="container-boe flex items-center justify-between h-20">
        {/* Logo */}
        <button
          onClick={() => handleNavClick("hero")}
          data-testid={NAV.logo}
          className="flex items-center gap-3 group"
        >
          <div
            className={`w-11 h-11 rounded-full flex items-center justify-center border transition-all group-hover:scale-105 ${
              onDark
                ? "bg-white/10 border-gold/50 backdrop-blur-md"
                : "bg-anthracite-soft border-gold/50"
            }`}
          >
            <span className="font-accent text-gold text-xl font-semibold leading-none">B</span>
          </div>
          <div className="flex flex-col leading-tight text-left">
            <span className={`font-semibold tracking-tight text-sm ${onDark ? "text-cream" : "text-cream"}`}>
              Brotherhood of Eden
            </span>
            <span className={`text-[10px] uppercase tracking-[0.25em] ${onDark ? "text-cream/60" : "text-cream/60"}`}>
              Schweiz
            </span>
          </div>
        </button>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-7">
          {sections.map((s) => (
            <button
              key={s.id}
              data-testid={NAV[s.key]}
              onClick={() => handleNavClick(s.id)}
              className={`text-sm font-medium transition-colors duration-200 ${
                onDark ? "text-cream/85 hover:text-gold-soft" : "text-cream/85 hover:text-gold-soft"
              }`}
            >
              {t.nav[s.key]}
            </button>
          ))}
        </nav>

        {/* Right cluster */}
        <div className="hidden lg:flex items-center gap-4">
          <div
            className={`flex items-center gap-1 px-1 py-1 rounded-full border ${
              onDark ? "bg-white/8 border-white/15 backdrop-blur-md" : "bg-white/8 border-white/15 backdrop-blur-md"
            }`}
          >
            {langs.map((l) => (
              <button
                key={l.code}
                onClick={() => setLang(l.code)}
                data-testid={NAV.langSwitch(l.code)}
                className={`px-2.5 py-1 text-xs font-semibold rounded-full transition-all duration-200 ${
                  lang === l.code
                    ? onDark
                      ? "bg-gold text-anthracite"
                      : "bg-gold text-anthracite"
                    : onDark
                      ? "text-cream/70 hover:text-cream"
                      : "text-cream/70 hover:text-cream"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setLoginOpen(true)}
            data-testid="header-login-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-cream/85 hover:text-gold-soft transition-colors"
          >
            <LogIn size={15} /> {t.nav.login}
          </button>
          <button
            onClick={() => handleNavClick("contact")}
            data-testid={NAV.join}
            className="btn-primary !py-2.5 !px-5"
          >
            {t.nav.join}
          </button>
        </div>

        {/* Mobile toggle */}
        <button
          className={`lg:hidden p-2 rounded-full ${onDark ? "text-cream" : "text-cream"}`}
          onClick={() => setOpen((v) => !v)}
          data-testid={NAV.mobileToggle}
          aria-label="menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-anthracite/95 backdrop-blur-xl border-t border-gold/15">
          <div className="container-boe py-6 flex flex-col gap-4">
            {[...sections, { id: "contact", key: "contact" }].map((s) => (
              <button
                key={s.id}
                onClick={() => handleNavClick(s.id)}
                className="text-left text-base font-medium py-2 text-cream/90"
              >
                {t.nav[s.key]}
              </button>
            ))}
            <div className="flex items-center gap-2 pt-2">
              {langs.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${
                    lang === l.code
                      ? "bg-gold text-anthracite border-gold"
                      : "border-white/15 text-cream/70"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => handleNavClick("contact")}
              className="btn-primary w-full mt-2"
            >
              {t.nav.join}
            </button>
            <button
              onClick={() => { setOpen(false); setLoginOpen(true); }}
              data-testid="header-login-btn-mobile"
              className="btn-secondary w-full inline-flex items-center justify-center gap-2"
            >
              <LogIn size={16} /> {t.nav.login}
            </button>
          </div>
        </div>
      )}

      <MemberLoginModal open={loginOpen} onOpenChange={setLoginOpen} />
    </header>
  );
};

export default Header;
